import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import { SelectChangeEvent } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";

import { AdminTypes } from "../../../components/Menu";
import { CustomAlert, CustomButton, CustomSelect, Picker } from "../../../shared/uiComponents";
import { SelectDataProperties } from "../../../shared/uiComponents/Dropdown";
import { dateNormalizer, errorNormalizer } from "../../../shared/Helpers/functions";
import { TimeInputsWrapper } from "../../Reports/helpers";
import { useResetCalendarData } from "../Helpers/hooks";

import { DispatchProperties, useSelector } from "../../../redux/store";
import { SessionDataProperties } from "../../../redux/API/ClientAPIHelpers/sessionsProperties";
import { getUserClientsStartSession } from "../../../redux/State/clientSlice/userClientSlice";
import { getSessionTypes, startSession } from "../../../redux/State/clientSlice/sessionSlice";
import { SessionTypeProperties } from "../../../redux/API/ClientAPIHelpers/calendarProperties";

const todayDayjs = dayjs();

const StartSession = () => {
  const [open, setOpen] = useState<boolean>(false);

  const closeHandler = () => {
    setOpen(false);
  };

  return (
    <div>
      <CustomButton title="Start session" onClick={() => setOpen(true)} />
      <CustomAlert
        open={open}
        onClose={closeHandler}
        title={"Add session"}
        Content={() => <StartSessionContent onClose={closeHandler} />}
      />
    </div>
  );
};

const StartSessionContent = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch<DispatchProperties>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { resetData } = useResetCalendarData();

  const userId = useSelector((state) => state.account.user.id);
  const sessionTypesData = useSelector((state) => state.session.sessionTypes);
  const role = useSelector((state) => state.account.role);
  const isBT = role?.section.id === AdminTypes.bt;

  const [clientList, setClientList] = useState<Array<SelectDataProperties>>([]);
  const [clientId, setClientId] = useState<string>("0");
  const [endTimeValue, setEndTimeValue] = useState<Dayjs>(todayDayjs.add(1, "hour"));
  const [pageSize, setPageSize] = useState<number>(8);
  const [sessionTypeId, setSessionTypeId] = useState<string>("0");
  const [sessionTypes, setSessionTypes] = useState<SessionTypeProperties[]>([]);

  const onClientChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setClientId(value);
  };

  const onTypeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSessionTypeId(value);
  };

  const onEndDateChange = (date: Dayjs | null) => {
    if (!date) return;
    setEndTimeValue(date);
  };

  const startSessionHandler = () => {
    if (!clientId || !sessionTypeId) return;

    const date = dateNormalizer(todayDayjs);

    const data: SessionDataProperties = {
      clientId,
      date,
      endTime: endTimeValue?.format("YYYY-MM-DDTHH:mm"),
      sessionTypeId: parseInt(sessionTypeId),
      startTime: todayDayjs.format("YYYY-MM-DDTHH:mm"),
    };
    dispatch(startSession(data))
      .then(unwrapResult)
      .then((response) => {
        if (!response.id || !response.client.id) return;
        onClose();
        resetData();
        navigate(`${pathname}/session/${response.id}/${response.client.id}`, {
          state: response,
        });
      })
      .catch(errorNormalizer);
  };

  useEffect(() => {
    if (!userId) return;
    dispatch(
      getUserClientsStartSession({
        userId,
        page: "1",
        pageSize: pageSize.toString(),
      })
    )
      .then(unwrapResult)
      .then((response) => {
        const { query } = response;
        if (!query) return;
        const clientList = query.map((client) => ({
          id: client.id as string,
          name: `${client.firstName} ${client.lastName}`,
        }));
        if (!clientList.length) {
          setClientId("");
          return;
        }
        setClientList([{ id: "0", name: "Select Client" }, ...clientList]);
      })
      .catch(errorNormalizer);
  }, [userId, pageSize, dispatch]);

  useEffect(() => {
    const data = sessionTypesData.filter((sessionType) =>
      sessionType.allowedRoleSections?.find((x) => x.id === role?.section.id)
    );
    setSessionTypes(data);
    if (!isBT && data.length !== 1) return;
    setSessionTypeId("3");
  }, [sessionTypesData, role, isBT]);

  useEffect(() => {
    if (clientId?.length < 2) return;
    dispatch(getSessionTypes({ clientId, date: dateNormalizer(todayDayjs), userId }));
  }, [dispatch, clientId, userId]);

  return (
    <>
      <CustomSelect
        label="Client"
        data={clientList}
        value={clientId}
        setValue={onClientChange}
        className={"marginBottom16"}
        loadMore={{
          activate: true,
          setSize: setPageSize,
        }}
      />
      {!!sessionTypes && (
        <CustomSelect
          label="Session type"
          data={sessionTypes}
          value={sessionTypeId}
          setValue={onTypeChange}
          className={"marginBottom16"}
          disabled={!clientId}
        />
      )}
      <Picker.CustomDate
        label={"Date of session"}
        onChange={() => {}}
        value={todayDayjs}
        className={"marginBottom16"}
        disabled={true}
      />
      <TimeInputsWrapper>
        <Picker.CustomTime
          label="Start time"
          value={todayDayjs}
          onChange={() => {}}
          disabled={true}
        />
        <Picker.CustomTime label="End time" value={endTimeValue} onChange={onEndDateChange} />
      </TimeInputsWrapper>

      <CustomButton
        onClick={startSessionHandler}
        title={"Start session"}
        disabled={!endTimeValue || !clientId || clientId === "0" || sessionTypeId === "0"}
      />
    </>
  );
};

export default StartSession;
